import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/videos";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { to: "/", label: "首页", icon: "ri-home-5-line" },
  { to: "/favorites", label: "收藏", icon: "ri-heart-3-line" },
  { to: "/about", label: "关于", icon: "ri-information-line" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { count } = useFavorites();

  useEffect(() => {
    if (searchOpen) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [searchOpen]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    setSearchOpen(false);
    setMobileOpen(false);
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="91抖音首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-play-circle-fill text-xl"></i>
            </span>
            <span className="font-heading text-xl font-bold tracking-tight text-foreground-950">
              91<span className="text-primary-500">抖音</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navItems.slice(0, 1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-50 font-medium text-primary-600"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                分类
                <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full w-64 pt-2">
                  <div className="animate-fade-in rounded-lg border border-background-200 bg-background-50 p-2">
                    <div className="grid grid-cols-2 gap-1">
                      {categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/category/${category.id}`}
                          onClick={() => setCategoryOpen(false)}
                          className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                        >
                          <i className={`${category.icon} text-base`}></i>
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {navItems.slice(1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `relative flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-50 font-medium text-primary-600"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
                {item.to === "/favorites" && count > 0 && (
                  <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[10px] text-background-50">
                    {count}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="ml-auto hidden max-w-xs flex-1 md:block">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3.5 py-2 focus-within:border-primary-300 focus-within:bg-background-50">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索视频、UP主"
                aria-label="搜索视频"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1 md:ml-0">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索框"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <Link
              to="/favorites"
              aria-label="我的收藏"
              className="relative flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className="ri-heart-3-line text-xl"></i>
              {count > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[10px] text-background-50">
                  {count}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="打开菜单"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-home-5-line"></i> 首页
            </Link>
            <div className="mt-1 px-3 text-xs font-medium uppercase tracking-wide text-foreground-400">
              视频分类
            </div>
            <div className="mt-1 grid grid-cols-3 gap-1.5">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 rounded-md bg-background-100 px-2 py-2.5 text-sm text-foreground-700"
                >
                  <i className={`${category.icon} text-base`}></i>
                  {category.name}
                </Link>
              ))}
            </div>
            <Link
              to="/favorites"
              onClick={() => setMobileOpen(false)}
              className="mt-3 flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-heart-3-line"></i> 我的收藏
              {count > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[10px] text-background-50">
                  {count}
                </span>
              )}
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-information-line"></i> 关于我们
            </Link>
          </div>
        )}
      </header>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 px-4 pt-24"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="w-full max-w-xl animate-fade-up rounded-lg bg-background-50 p-4"
            onClick={(event) => event.stopPropagation()}
          >
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 focus-within:border-primary-300 focus-within:bg-background-50">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  ref={inputRef}
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入视频标题关键词，回车搜索"
                  aria-label="搜索视频"
                  className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门关键词：</span>
              {["街舞", "吉他", "萌宠", "美食", "古风"].map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => setKeyword(word)}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}